import { memo } from 'react';
import { AlertTriangle, ChevronRight, FileSignature } from 'lucide-react';
import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { VideoButton } from '@/components/VideoButton';
import { type CalculatorAnketaType } from '@pomoshnik/shared';
import { ANKETA_TYPE_CARDS, type AnketaTypeCard } from './anketaTypeCards';

export interface CalculatorDraftEntry {
  id: string;
  name: string;
  anketaType: CalculatorAnketaType;
  /** PR-H3 (ADR-0028) — для display «N минут назад» + sort внутри group. */
  updatedAt: number;
}

/**
 * PR-H3 (ADR-0028) — drafts grouped by anketaType для drafts list на CalculatorEntry.
 * `drafts` отсортированы DESC по `updatedAt` (most recent сверху).
 */
export interface CalculatorDraftGroup {
  anketaType: CalculatorAnketaType;
  /** Human-readable label из CALCULATOR_ANKETA_TYPE_LABELS — для группы header'а. */
  typeLabel: string;
  drafts: CalculatorDraftEntry[];
}

export interface CalculatorEntryViewProps {
  /**
   * PR-H3 (ADR-0028) — grouped drafts list. Пустой `[]` → секция «Черновики» НЕ рендерится.
   * Заменил Phase A6 single-draft `draft` prop (deprecated в PR-H3).
   */
  draftsByType: CalculatorDraftGroup[];
  /** Click обработчик на любую из 8 anketa-карточек. */
  onSelectType: (type: CalculatorAnketaType) => void;
  /** Click обработчик на кнопку «Коммерческое предложение». */
  onOpenQuote: () => void;
  /**
   * Click обработчик на draft item в списке «Черновики».
   * PR-H3 (ADR-0028): signature `(draftId, anketaType)` — type приходит из callback,
   * не из closure'а single draft, потому что drafts list может иметь несколько типов.
   */
  onResumeDraft: (draftId: string, anketaType: CalculatorAnketaType) => void;
  /**
   * Optional click обработчик на «Что улучшить» в BottomBar.
   * Если undefined — BottomBar использует default-navigate flow (ADR-0018 §Q9):
   * `navigate('/feedback', { state })` с context из pathInference.
   */
  onFeedback?: () => void;
}

/**
 * Презентационный компонент Entry Screen 2.12.1 «Калькулятор».
 *
 * Структура (PR-H3 / ADR-0028 layout):
 *   1. Sticky Header (title="Калькулятор") — OnlineIndicator в правом углу через Phase A5.
 *   2. VideoButton (первый контент per CLAUDE.md §16.4 — карточка YouTube-инструкции).
 *   3. Disclaimer card (warning) — «Расчёт примерной стоимости. ±10%».
 *   4. Greeting heading «Что считаем?».
 *   5. 8 TypeCards vertical list (P-022 vertical buttons only).
 *   6. PR-H3: NEW «Черновики» section (если draftsByType.length > 0) —
 *      grouped по anketaType, drafts DESC по updatedAt.
 *   7. Divider «Уже посчитали?».
 *   8. КП-кнопка (FileSignature icon) — «Коммерческое предложение».
 *   9. Sticky BottomBar (3 кнопки: Назад / На главную / Что улучшить, P-023).
 *
 * PR-H3 удалил Phase A6 single «Продолжить расчёт» banner — заменён drafts list
 * который scale'ится на несколько проектов per anketaType (см. ADR-0028 §Решение).
 *
 * MUST: memo wrap (re-render редкий — профилактика layout thrash при
 * useOnlineStatus refresh каждые 30s).
 *
 * MUST: TypeCards и draft items — `<button>` элементы вертикальным списком (P-022).
 * Не использовать `<a>` или `<div onClick>`.
 *
 * MUST: все min-h-tap (44px tap target, P-022).
 *
 * VideoButton videoUrl — placeholder URL (как в Block 10 entry — заменим в Phase D).
 * Не использовать inline styles — только Tailwind utilities.
 */
export const CalculatorEntryView = memo(function CalculatorEntryView({
  draftsByType,
  onSelectType,
  onOpenQuote,
  onResumeDraft,
  onFeedback,
}: CalculatorEntryViewProps) {
  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title="Калькулятор" />
      <section className="flex-1 overflow-y-auto pb-20 px-3 pt-3 flex flex-col gap-3">
        <VideoButton videoUrl="https://www.youtube.com/embed/dQw4w9WgXcQ" />

        <div
          role="note"
          aria-label="Внимание — расчёт примерной стоимости"
          className="flex items-start gap-2 rounded-xl bg-warning/10 border border-warning/40 px-4 py-3"
        >
          <AlertTriangle className="flex-shrink-0 text-warning" size={20} aria-hidden="true" />
          <span className="text-base text-ink leading-snug">
            Расчёт примерной стоимости. Погрешность <strong>±10%</strong>. Закладывайте запас 10%.
          </span>
        </div>

        <h2 className="font-display text-h2 text-ink mt-2">Что считаем?</h2>

        <ul role="list" className="flex flex-col gap-2.5 mt-1">
          {ANKETA_TYPE_CARDS.map((card) => (
            <li key={card.anketaType}>
              <TypeCard card={card} onClick={() => onSelectType(card.anketaType)} />
            </li>
          ))}
        </ul>

        {draftsByType.length > 0 && (
          <section
            aria-labelledby="drafts-heading"
            className="bg-card rounded-2xl border-[1.5px] border-accent shadow-card p-4 mt-2"
          >
            <h2 id="drafts-heading" className="font-display text-h2 text-ink mb-3">
              Черновики
            </h2>
            {draftsByType.map((group) => (
              <div key={group.anketaType} className="mb-3 last:mb-0">
                <h3 className="text-sm font-semibold text-muted uppercase tracking-wider mb-1.5">
                  {group.typeLabel}
                </h3>
                <ul role="list" className="flex flex-col gap-1.5">
                  {group.drafts.map((draft) => {
                    const displayName = displayDraftName(draft, group.typeLabel);
                    return (
                      <li key={draft.id}>
                        <button
                          type="button"
                          onClick={() => onResumeDraft(draft.id, draft.anketaType)}
                          aria-label={`Открыть черновик «${displayName}»`}
                          className="w-full min-h-tap text-left rounded-xl border-[1.5px] border-accent bg-card hover:bg-canvas px-3 py-2 flex items-center justify-between gap-2 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90"
                        >
                          <span className="flex-1 min-w-0 flex flex-col gap-0.5">
                            <span className="text-base font-semibold text-ink truncate">
                              {displayName}
                            </span>
                            <span className="text-xs text-muted">
                              {formatRelative(draft.updatedAt)}
                            </span>
                          </span>
                          <ChevronRight
                            aria-hidden="true"
                            size={16}
                            className="text-muted flex-shrink-0"
                          />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </section>
        )}

        <div className="flex items-center gap-3 my-3" aria-hidden="true">
          <div className="flex-1 h-px bg-accent" />
          <span className="uppercase tracking-wider text-sm font-bold text-muted whitespace-nowrap">
            Уже посчитали?
          </span>
          <div className="flex-1 h-px bg-accent" />
        </div>

        <button
          type="button"
          onClick={onOpenQuote}
          aria-label="Открыть коммерческое предложение"
          className="w-full flex items-center gap-3 min-h-tap rounded-xl bg-card border-[1.5px] border-accent shadow-card text-ink px-4 py-3 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90"
        >
          <span
            aria-hidden="true"
            className="flex-shrink-0 w-10 h-10 rounded-full bg-cta text-card flex items-center justify-center"
          >
            <FileSignature size={20} />
          </span>
          <span className="flex-1 text-left">
            <span className="block text-base font-semibold leading-tight">
              Коммерческое предложение
            </span>
            <span className="block text-sm text-muted mt-0.5">
              Сформировать КП по готовому расчёту
            </span>
          </span>
          <ChevronRight aria-hidden="true" size={20} className="text-muted flex-shrink-0" />
        </button>
      </section>
      <BottomBar variant="default" onFeedback={onFeedback} />
    </main>
  );
});

interface TypeCardProps {
  card: AnketaTypeCard;
  onClick: () => void;
}

function TypeCard({ card, onClick }: TypeCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Открыть калькулятор «${card.label}»`}
      className="w-full flex items-center gap-3 min-h-tap rounded-xl bg-card border-[1.5px] border-accent shadow-card text-ink px-4 py-3 focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90"
    >
      <span className="flex-1 text-left">
        <span className="block text-base font-semibold leading-tight">{card.label}</span>
        <span className="block text-sm text-muted mt-0.5">{card.description}</span>
      </span>
      <ChevronRight aria-hidden="true" size={20} className="text-muted flex-shrink-0" />
    </button>
  );
}

/**
 * PR-H3 (ADR-0028 Q2): legacy projects без trailing «№N» pattern (например
 * `'(без названия)'` placeholder из `useAnketaPersist.stateToRecord`, или empty
 * string) → fallback display name «{typeLabel} №?». Read-only at render time,
 * НЕ persisted в IDB (future PR-H3.5 может migrate если потребуется).
 */
function displayDraftName(draft: CalculatorDraftEntry, typeLabel: string): string {
  if (draft.name && /№\d+$/.test(draft.name)) {
    return draft.name;
  }
  return `${typeLabel} №?`;
}

/**
 * Простой relative time formatter для drafts list timestamps.
 * Локалезависимое API (Intl.RelativeTimeFormat) избыточно для текущего scope
 * (RU-only fallback labels, future LinguiJS phase).
 */
function formatRelative(timestamp: number): string {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHour = Math.floor(diffMs / 3_600_000);
  const diffDay = Math.floor(diffMs / 86_400_000);

  if (diffMin < 1) return 'только что';
  if (diffMin < 60) return `${diffMin} мин назад`;
  if (diffHour < 24) return `${diffHour} ч назад`;
  if (diffDay === 1) return 'вчера';
  if (diffDay < 7) return `${diffDay} д назад`;
  const date = new Date(timestamp);
  const dd = date.getDate().toString().padStart(2, '0');
  const mm = (date.getMonth() + 1).toString().padStart(2, '0');
  return `${dd}.${mm}.${date.getFullYear()}`;
}
